<template>
  <BottomSheet ref="bottomSheet" :fullscreen="true">
    <div class="apple-price-comparison">
      <h2 class="apple-price-comparison__title">Choose a payment method</h2>
      <p class="apple-price-comparison__subtitle">
        All payment methods are 100% secure.
      </p>

      <BillingApplePriceCard16218
        class="apple-price-comparison__card"
        :plan="plan"
        :isExternal="false"
        @subscribe="appStoreSubscribe"
      />
      <BillingApplePriceCard16218
        class="apple-price-comparison__card"
        :plan="stripePlan"
        :isExternal="true"
        @subscribe="openExternalPaymentPage"
      />

      <Transition name="fade">
        <div
          v-if="isSubscribing"
          class="apple-price-comparison__loading-screen"
          @touchmove.stop
        >
          <div class="apple-price-comparison__loading-inner">
            <CircleSpinner :size="24" />
            <p class="apple-price-comparison__loading-text">Loading...</p>
          </div>
        </div>
      </Transition>
    </div>
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Browser } from '@capacitor/browser'

import { AppStoreBillingView, PlanView } from '@/models/billing'
import { assertNotNull } from '@/helpers/typing'
import { auth } from '@/services/auth'
import { emitter } from '@/services/mitt'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'
import BottomSheet from '@/app/components/BottomSheet.vue'
import BillingApplePriceCard16218 from './BillingApplePriceCard16218.vue'
import { platform } from '@/services/platform'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  billing: AppStoreBillingView
  plan: PlanView
}>()

const emit = defineEmits(['appStoreSubscribe'])

const bottomSheet = ref<InstanceType<typeof BottomSheet> | null>(null)

const isSubscribing = ref(false)

const stripePlan = computed(() => {
  const plan =
    props.plan.id === 'plan_annual'
      ? props.billing.stripeAnnualPlan16218
      : props.billing.stripeMonthlyPlan16218

  return assertNotNull(plan)
})

function appStoreSubscribe(): void {
  isSubscribing.value = true
  Mixpanel.trackAppStoreButton(props.plan)
  emit('appStoreSubscribe')
}

/**
 * The iOS external payment flow.
 *
 * Open the Stripe subscription popup in a new in-app browser window (we can't
 * open it directly in the app due to the App Store's rules).
 * https://github.com/shortformhq/main/issues/16218
 */
async function openExternalPaymentPage(): Promise<void> {
  isSubscribing.value = true
  Mixpanel.trackExternalPaymentButton(props.plan)

  const baseUrl = window.location.origin
  const paymentPageUrl = new URL(`${baseUrl}/app/billing-apple-external`)
  const authToken = assertNotNull(auth.getToken())
  const deviceInfo = await platform.deviceInfo()

  // The page in the in-app browser is a different container,
  // so the user will not be logged in. We pass the auth token
  // in the URL query so that the user doesn't have to manually
  // log in again.
  paymentPageUrl.searchParams.set('authToken16218', authToken)
  // The selected plan's ID.
  paymentPageUrl.searchParams.set('plan16218', props.plan.id)
  // The app version (for mocking the device info we send to the backend in
  // the `savePaymentMethod` request).
  paymentPageUrl.searchParams.set('appVersion16218', deviceInfo.appVersion)

  Browser.open({ url: paymentPageUrl.toString(), presentationStyle: 'popover' })

  // When the in-app browser is closed automatically after subscribing.
  emitter.on('closeInAppBrowser16218', updateBilling)
  // When the in-app browser is closed manually by the user.
  Browser.addListener('browserFinished', updateBilling)
}

async function updateBilling(): Promise<void> {
  emitter.off('closeInAppBrowser16218', updateBilling)
  Browser.removeAllListeners()

  await props.billing.updateAfterExternalPage16218(props.plan, close)
  isSubscribing.value = false
}

function show(): void {
  isSubscribing.value = false
  bottomSheet.value?.view.open()
}

function close(): void {
  bottomSheet.value?.view.close()
}

defineExpose({
  show,
  close,
})
</script>

<style lang="scss" scoped>
.apple-price-comparison {
  max-width: 500px;
  margin-inline: auto;

  &__title {
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    letter-spacing: -0.02em;
    margin-bottom: 4px;
  }

  &__subtitle {
    text-align: center;
    margin-bottom: 16px;
  }

  &__card {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__loading-screen {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.3);
  }

  &__loading-inner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: -32px;
    padding: 16px 32px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(3px);
  }

  &__loading-text {
    margin: 0;
    font-size: 16px;
    letter-spacing: 0.02em;
    color: $c-dark-4;
  }
}

.night-mode .apple-price-comparison {
  &__subtitle {
    color: $c-black-6;
  }
}
</style>
